<template>
  <div class="main-nav-bar manager-main-nav-bar">
    <v-list
      v-if="navOptions?.tabs"
      key="standardNavigation"
      color="black"
      open-strategy="single-leaf"
    >
      <template v-if="navOptions.backRoute">
        <NavItem
          v-if="navOptions.backRoute"
          :action="goBackFromNestedNav"
          :title="navOptions.backTitle || 'Back'"
          icon="chevron_left"
        />
        <v-divider class="w90pc mx-auto" />
      </template>

      <template v-if="navOptions.queryDriven">
        <TabHeader
          v-for="navTab in navOptions.tabs"
          @click="updateTab(navTab.queryValue)"
          :key="navTab.title"
          :active="route.query?.tab === navTab.queryValue"
          :data-cy="`${navTab.queryValue}_tab`"
          :data-testid="`${navTab.queryValue}_tab`"
          :hide-icon="navTab.hideIcon"
          :locked="navTab.locked"
          :progress="progressPercentForSection(navTab.queryValue)"
          :title="navTab.title"
        />
      </template>

      <template v-else>
        <NavItem
          v-for="navTab in navOptions.tabs"
          :key="navTab.title"
          :icon="navTab.icon"
          :route="navTab.route"
          :title="navTab.title"
        />
      </template>
    </v-list>

    <Teleport
      to="#manager-main-nav-footer-mount"
      defer
    >
      <div
        v-if="showAccountSettingsNavLink"
        class="manager-main-nav-append-footer manager-main-nav-bar"
      >
        <v-divider class="w90pc mx-auto" />
        <v-list
          class="pa-0"
          color="black"
        >
          <NavItem
            :route="accountSettingsRoute"
            icon="settings"
            title="Account settings"
            large
            nested-nav
          />
        </v-list>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';

import NavItem from '@/shared/components/navigation/NavItem.vue';
import TabHeader from '@/shared/components/navigation/TabHeader.vue';
import useApi from '@/shared/composables/useApi';
import { useProviderProfileSectionProgress } from '@/manager/composables/useProviderProfileSectionProgress';
import useTerms from '@/shared/composables/useTerms';

const { api } = useApi();
const route = useRoute();
const router = useRouter();
const store = useStore();
const { terms } = useTerms();

const providerIdForProgress = computed(() => route.params.providerId);
const { progressPercentForSection } = useProviderProfileSectionProgress(providerIdForProgress);

const props = defineProps({
  showAccountSettingsNav: {
    type: Boolean,
    default: false,
  },
});

const provider = ref(null);

watch(() => route.params.providerId, loadProvider, { immediate: true });

const navOptions = computed(() => {
  if (isEditingProviderProfile.value) {
    return providerProfileNavOptions.value ?? { tabs: null };
  }
  if (props.showAccountSettingsNav) return store.getters.accountSettingNavOptions();
  return store.getters.primaryNavigationOptions(route);
});

const providerProfileNavOptions = computed(() => {
  if (!isEditingProviderProfile.value) return null;
  if (!provider.value) return null;

  return store.getters.providerProfileNavOptions(provider.value, terms.value);
});

const isEditingProviderProfile = computed(() => {
  return route.name === 'ProviderShow';
});

/** Shown below primary section links — hidden for provider profile and account-settings sub-nav. */
const showAccountSettingsNavLink = computed(() => {
  return !isEditingProviderProfile.value && !props.showAccountSettingsNav;
});

const showBusinessPages = computed(() => {
  return store.state.pages.Settings.enabled && store.getters.showBusinessAdminPages();
});

const accountSettingsRoute = computed(() => {
  if (showBusinessPages.value) return 'BusinessShow';
  return 'ManagerAccount';
});

async function goBackFromNestedNav() {
  await router.push(navOptions.value.backRoute);
}

async function loadProvider() {
  if (!route.params.providerId) return;

  const response = await api.manager.provider.get(route.params.providerId);
  if (response?.status !== 200) return;

  provider.value = response.data;
}

async function updateTab(newTab) {
  await router.replace({ query: { tab: newTab } });
}
</script>

<style scoped>
.manager-main-nav-bar :deep(.v-list-item--active) {
  background-color: transparent !important;
}

.manager-main-nav-bar :deep(.v-list-item:not(:hover):not(:focus-visible) .v-list-item__overlay) {
  opacity: 0 !important;
}

.manager-main-nav-bar :deep(.v-list-item.v-list-item--link *) {
  text-decoration: none !important;
}

.manager-main-nav-bar :deep(.v-list-item .v-list-item-title span.fs-16) {
  font-weight: 500 !important;
}

.manager-main-nav-bar :deep(.v-list-item--active .v-list-item-title span.fs-16) {
  font-weight: 600 !important;
  color: black;
}
</style>
